import { useEffect, useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Copy, WhatsappLogo, ArrowRight, Confetti } from '@phosphor-icons/react';
import toast from 'react-hot-toast';

import styles from './ShareScreen.module.css';

interface ShareScreenProps {
    userSlug: string;
    pollSlug: string;
    pollTitle: string;
    onViewPoll: () => void;
}

interface Particle {
    id: number;
    x: number;
    color: string;
    delay: number;
    size: number;
}

const CONFETTI_COLORS = [
    'var(--option-color-1)',
    'var(--option-color-2)',
    'var(--option-color-3)',
    'var(--option-color-4)',
    'var(--option-color-5)',
    'var(--option-color-6)',
];

export function ShareScreen({ userSlug, pollSlug, pollTitle, onViewPoll }: ShareScreenProps) {
    const [particles, setParticles] = useState<Particle[]>([]);
    const pollUrl = `${window.location.origin}/${userSlug}/${pollSlug}`;

    useEffect(() => {
        const generated: Particle[] = Array.from({ length: 30 }, (_, i) => ({
            id: i,
            x: Math.random() * 100,
            color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
            delay: Math.random() * 0.8,
            size: 6 + Math.random() * 8,
        }));
        setParticles(generated);
    }, []);

    const handleCopy = useCallback(async () => {
        try {
            await navigator.clipboard.writeText(pollUrl);
            toast.success('Enlace copiado');
        } catch {
            toast.error('No se pudo copiar el enlace');
        }
    }, [pollUrl]);

    const handleWhatsApp = useCallback(() => {
        window.open(
            `https://wa.me/?text=${encodeURIComponent(`Vota en mi encuesta "${pollTitle}": ${pollUrl}`)}`,
            '_blank'
        );
    }, [pollTitle, pollUrl]);

    return (
        <motion.div
            className={styles.container}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.4 }}
        >
            {/* Confetti */}
            <div className={styles.confetti} aria-hidden="true">
                {particles.map((p) => (
                    <motion.div
                        key={p.id}
                        className={styles.particle}
                        style={{
                            left: `${p.x}%`,
                            width: p.size,
                            height: p.size,
                            backgroundColor: p.color,
                            borderRadius: Math.random() > 0.5 ? '50%' : '2px',
                        }}
                        initial={{ y: -20, opacity: 1, rotate: 0 }}
                        animate={{ y: '100vh', opacity: 0, rotate: 360 + Math.random() * 360 }}
                        transition={{
                            duration: 2.5 + Math.random() * 1.5,
                            delay: p.delay,
                            ease: 'easeIn',
                        }}
                    />
                ))}
            </div>

            <motion.div
                className={styles.content}
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
            >
                <Confetti size={48} weight="duotone" className={styles.celebrationIcon} />
                <h1 className={styles.heading}>¡Listo!</h1>
                <p className={styles.subtitle}>Tu encuesta esta lista para compartir</p>

                <div className={styles.urlBox}>
                    <span className={styles.urlText}>{pollUrl}</span>
                </div>

                <div className={styles.actions}>
                    <motion.button
                        className={styles.copyBtn}
                        onClick={handleCopy}
                        whileTap={{ scale: 0.95 }}
                    >
                        <Copy size={20} weight="bold" />
                        Copiar enlace
                    </motion.button>

                    <motion.button
                        className={styles.whatsappBtn}
                        onClick={handleWhatsApp}
                        whileTap={{ scale: 0.95 }}
                    >
                        <WhatsappLogo size={20} weight="fill" />
                        Compartir por WhatsApp
                    </motion.button>
                </div>

                <button className={styles.viewLink} onClick={onViewPoll}>
                    Ver encuesta
                    <ArrowRight size={16} />
                </button>
            </motion.div>
        </motion.div>
    );
}
